import { c as _c } from "react/compiler-runtime";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useGiveXgSeats } from "@hooks/use-give-xg-seats";
import { areCompanyMembersLoadingSelector, companyMembersSelector, fetchedMembersFlagsSelector } from "@store/members/members-selector";
import { activeSeatsAvailableSelector, isScanPlanSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { getSelectedOptionValue, getSelectedOptionValues } from "@utils/members-invite-utils";
import { getBlockedMessage, getMembersByKey, getSelectionXgSeats, getTeamKeys, getXgSeatChipColor, NO_XG_SEAT_MESSAGE, TEAM_NOT_ALLOWED_MESSAGE } from "@utils/invite-xg-seat-utils";
import { nounPluralize } from "@utils/data-display";
import { useState } from "react";

/** The checkbox that gives the selected members without one a Sphere XG seat along the invite */

/** How giving the seats to the selection went */

/** How the selection fares with the Sphere XG seats that the selected role needs */

/**
 * Returns the keys that the user agrees to give the people a seat with: their lower-cased emails.
 * Unlike an identity, the email of somebody outside the workspace stays the same once they join it,
 * so the consent holds when they need their seat again, e.g. when the invite is retried.
 */
function getSeatKeys({
  membersToSeat,
  emailsToSeat
}) {
  return [...membersToSeat.map(({
    email
  }) => email), ...emailsToSeat].map(email => email.toLowerCase());
}

/** Giving no seats */
const NO_FAILURES = {
  failures: [],
  numberOfSeatsGiven: 0,
  failedValues: new Set()
};

/** A selection that needs no seats, because of the plan or the role */
const SELECTION_WITHOUT_SEATS = {
  getChipColor: () => undefined,
  isValid: true,
  giveSeats: () => Promise.resolve(NO_FAILURES)
};

/**
 * Applies the Sphere XG seats to inviting members, e.g. to a project or a group. On a Scan plan,
 * only members that hold a Sphere XG seat can be given a role that needs one, and teams cannot be
 * given such a role at all.
 *
 * Users that manage the seats may give one to the selected members without, as long as the
 * workspace has enough free seats. Everybody else, and they too once no seat is left, can only
 * select the members holding one.
 */
export function useInviteXgSeats(t0) {
  const $ = _c(22);
  const {
    isSeatRequiredForRole,
    seatRequiredMessage,
    location
  } = t0;
  const {
    giveSeats
  } = useGiveXgSeats(location);
  const {
    canManageXgSeats
  } = useHasUserValidRoleCompanyLevel();
  const isScanPlan = useAppSelector(isScanPlanSelector);
  const companyMembers = useAppSelector(companyMembersSelector);
  const teams = useAppSelector(assignableTeamsSelector);
  const seatsAvailable = useAppSelector(activeSeatsAvailableSelector);
  const areMembersLoading = useAppSelector(areCompanyMembersLoadingSelector);
  const {
    hasFetchedWorkspaceMembers
  } = useAppSelector(fetchedMembersFlagsSelector);
  const [consentedKeys, setConsentedKeys] = useState(_temp);
  const isSeatRequired = isScanPlan && isSeatRequiredForRole;
  const isOutOfSeats = seatsAvailable === 0;
  const canGiveSeats = canManageXgSeats && !isOutOfSeats;
  let t1;
  if ($[0] !== companyMembers) {
    t1 = getMembersByKey(companyMembers);
    $[0] = companyMembers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const membersByKey = t1;
  let t2;
  if ($[2] !== teams) {
    t2 = getTeamKeys(teams);
    $[2] = teams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const teamKeys = t2;
  let checkSelection;
  if ($[4] !== areMembersLoading || $[5] !== canGiveSeats || $[6] !== canManageXgSeats || $[7] !== consentedKeys || $[8] !== giveSeats || $[9] !== hasFetchedWorkspaceMembers || $[10] !== isOutOfSeats || $[11] !== isSeatRequired || $[12] !== membersByKey || $[13] !== seatRequiredMessage || $[14] !== seatsAvailable || $[15] !== teamKeys) {
    checkSelection = function checkSelection(selectedOptions) {
      if (!isSeatRequired) {
        return SELECTION_WITHOUT_SEATS;
      }
      const selectedValues = getSelectedOptionValues(selectedOptions);
      const selection = getSelectionXgSeats({
        selectedValues,
        membersByKey,
        teamKeys,
        areAllMembersKnown: hasFetchedWorkspaceMembers,
        canGiveSeats
      });
      const {
        blockedValues,
        membersToSeat,
        emailsToSeat
      } = selection;
      const seatKeys = getSeatKeys(selection);
      const hasEnoughSeats = hasSeatsFor(seatKeys.length);
      const areSeatsGiven = isConsentGiven(seatKeys) && hasEnoughSeats;
      const isValidOtherwise = !areMembersLoading && !blockedValues.size;
      return {
        getChipColor: option => getXgSeatChipColor(getSelectedOptionValue(option), selection, hasEnoughSeats),
        helperText: getBlockedHelperText(blockedValues),
        isValid: isValidOtherwise && (!seatKeys.length || areSeatsGiven),
        giveSeatsCheckbox: getGiveSeatsCheckbox(seatKeys, areSeatsGiven),
        consentMissingMessage: isValidOtherwise && seatKeys.length > 0 && hasEnoughSeats && !areSeatsGiven ? `Assign the ${nounPluralize({
          counter: seatKeys.length,
          word: "seat",
          shouldShowCounter: false
        })} to continue` : undefined,
        giveSeats: async () => {
          if (!areSeatsGiven) {
            return NO_FAILURES;
          }
          const givenSeats = await giveSeats({
            members: membersToSeat,
            emails: emailsToSeat
          });
          const failedKeys = new Set(givenSeats.failures.map(_temp2));
          return {
            ...givenSeats,
            failedValues: new Set(selectedValues.filter(value => failedKeys.has(getSeatKey(value))))
          };
        }
      };
    };
    function getSeatKey(value_0) {
      const member = membersByKey.get(value_0) ?? membersByKey.get(value_0.toLowerCase());
      return member?.identity ?? value_0.toLowerCase();
    }
    function isConsentGiven(keys) {
      return !!keys.length && keys.every(key_0 => consentedKeys.has(key_0));
    }
    function hasSeatsFor(numberOfPeople) {
      return seatsAvailable === undefined || numberOfPeople <= seatsAvailable;
    }
    function getBlockedHelperText(blockedValues_0) {
      if (!blockedValues_0.size) {
        return;
      }
      const text = getBlockedMessage({
        reasons: new Set(blockedValues_0.values()),
        isOutOfSeats: canManageXgSeats && isOutOfSeats,
        seatRequiredMessage
      });
      return {
        type: "error",
        text
      };
    }
    function getGiveSeatsCheckbox(keys_0, areSeatsGiven_0) {
      if (!keys_0.length) {
        return;
      }
      return {
        numberOfMembers: keys_0.length,
        seatsAvailable,
        isChecked: areSeatsGiven_0,
        onChange: isNowChecked => setConsentedKeys(new Set(isNowChecked ? keys_0 : []))
      };
    }
    $[4] = areMembersLoading;
    $[5] = canGiveSeats;
    $[6] = canManageXgSeats;
    $[7] = consentedKeys;
    $[8] = giveSeats;
    $[9] = hasFetchedWorkspaceMembers;
    $[10] = isOutOfSeats;
    $[11] = isSeatRequired;
    $[12] = membersByKey;
    $[13] = seatRequiredMessage;
    $[14] = seatsAvailable;
    $[15] = teamKeys;
    $[16] = checkSelection;
  } else {
    checkSelection = $[16];
  }
  const t3 = isSeatRequired && !canGiveSeats ? NO_XG_SEAT_MESSAGE : undefined;
  const t4 = isSeatRequired ? TEAM_NOT_ALLOWED_MESSAGE : undefined;
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setConsentedKeys(new Set());
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== checkSelection || $[19] !== t3 || $[20] !== t4) {
    t6 = {
      memberWithoutSeatDisabledMessage: t3,
      teamDisabledMessage: t4,
      checkSelection,
      reset: t5
    };
    $[18] = checkSelection;
    $[19] = t3;
    $[20] = t4;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}
function _temp2(t0) {
  const {
    key
  } = t0;
  return key;
}
function _temp() {
  return new Set();
}